import Dialog from "@mui/material/Dialog";
import {
	AlertType,
	ApplicationScreen,
	BannedPatient,
	Clinic
} from "../../models";
import CircularProgress from "@mui/material/CircularProgress";
import { useCallback, useEffect, useState } from "react";
import { apiAccessRequest } from "../../constants";
import { useAlertContext, useUserContext } from "../../contexts";
import { useMsal } from "@azure/msal-react";
import { useCreateAuditLog } from "../../hooks";

interface ConfirmDeletePatientProps {
	patient: BannedPatient;
	clinic?: Clinic;
	open: boolean;
	onClose: (updated: boolean) => void;
}

export const ConfirmDeletePatient = ({
	patient,
	clinic,
	open,
	onClose
}: ConfirmDeletePatientProps) => {
	const { account } = useUserContext();
	const { instance } = useMsal();
	const { showAlert } = useAlertContext();
	const { createAuditLog } = useCreateAuditLog();
	const [isError, setIsError] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		setIsError(false);
		setIsLoading(false);
	}, [patient]);

	const createAuditEntry = useCallback(
		async (patient: BannedPatient) => {
			try {
				await createAuditLog([
					{
						clinicId: clinic?.id,
						username: account?.name ?? "",
						applicationScreen: ApplicationScreen.AdminBannedPatient,
						action: `Deleted banned patient id:${patient.bpPatientId} name: ${patient.patientName}`
					}
				]);
			} catch (error) {
				console.log("Error creating audit log", error);
			}
		},
		[account?.name, clinic?.id, createAuditLog]
	);

	const handleConfirmClick = async () => {
		setIsError(false);
		setIsLoading(true);
		try {
			const requestUrl = `${process.env.REACT_APP_CONSULTS_URL}patient/bannedpatient/${patient.id}`;
			const tokenResponse = await instance.acquireTokenSilent({
				account,
				scopes: apiAccessRequest.scopes
			});
			const headers = {
				Authorization: `Bearer ${tokenResponse.accessToken}`,
				"Content-Type": "application/json"
			};

			const response = await fetch(requestUrl, {
				method: "DELETE",
				headers
			});

			if (!response.ok) {
				setIsError(true);
				return;
			}
			await createAuditEntry(patient);
			onClose(true);
			showAlert({
				message: `${patient.patientName} has been removed from banned patients`,
				alertType: AlertType.success,
				timeout: true
			});
		} catch (error) {
			setIsError(true);
			showAlert({
				message: `There was an error removing ${patient.patientName} from banned patients`,
				alertType: AlertType.error
			});
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Dialog open={open}>
			<div
				className="flex flex-col items-center p-5 gap-4"
				style={{ width: 500 }}
			>
				{!isError && (
					<>
						<p className="ch-m-text ch-em-text-navy text-center">
							Remove Banned Patient
						</p>
						<p className="pb-3 text-center">
							You are going to remove {patient.patientName} from the banned
							patients llist.
						</p>

						<p className="w-full pb-3">
							Notes
							<textarea
								className="ch-border w-full"
								style={{ resize: "none" }}
								rows={4}
								value={patient.notes}
							/>
						</p>
						<div className="flex gap-4">
							{!isLoading && (
								<>
									<button
										className="ch-button ch-button-primary justify-center"
										onClick={() => onClose(false)}
									>
										Go Back
									</button>
									<button
										className="ch-button ch-button-secondary justify-center"
										onClick={() => handleConfirmClick()}
									>
										Confirm
									</button>
								</>
							)}
							{isLoading && (
								<div className="flex justify-center py-3">
									<CircularProgress size="80px" />
								</div>
							)}
						</div>
					</>
				)}
				{isError && (
					<div className="flex flex-col items-center py-3">
						<img
							src="/images/svg/road-sign-warning.svg"
							height="50"
							alt="error icon"
						/>
						<p className="ch-m-text py-3 text-center">
							An error has occurred. The banned patient has not been removed.
							Try again later
						</p>
						<div className="flex gap-6">
							<button
								className="ch-button ch-button-primary"
								onClick={() => onClose(false)}
							>
								Go back
							</button>
						</div>
					</div>
				)}
			</div>
		</Dialog>
	);
};
